"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import Link from "next/link";
import { EntityDisplay, type EntityDisplayDecorator } from "fansunited-sports-ui";
import type { FUSportsLineupMember } from "fansunited-data-layer";
import { getLabel } from "@/lib/labels/getLabel";
import type { Labels } from "@/lib/labels/types";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { useMatchPageData } from "../../context/MatchPageDataContext";
import { useNavigation } from "@/lib/navigation/NavigationProvider";
import type { PlayerEvents } from "./playerEventsHelper";
import { GoalIcon, YellowCardIcon, RedCardIcon, SubOutIcon, SubInIcon } from "@/components/ui/icons/PlayerEventIcons";
import { initialsFromName } from "@/utilities/formatting/playerInitials";

/**
 * Fallback rendered when a player has no headshot photo. Shows the
 * player's initials on a solid muted background, same visual pattern as
 * the match events timeline and squad views.
 */
const PlayerInitialsCircle: React.FC<{
  name: string;
}> = t0 => {
  const $ = _c(5);
  const {
    name
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = initialsFromName(name);
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== name || $[3] !== t1) {
    t2 = <div role="img" aria-label={name} className="w-full h-full rounded-full flex items-center justify-center font-semibold uppercase bg-muted-foreground text-background select-none text-sm">{t1}</div>;
    $[2] = name;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
interface LineupsPlayerRowProps {
  player: FUSportsLineupMember;
  isHome: boolean;
  labels: Labels;
  playerEvents?: PlayerEvents;
  /** If true, hide the bottom border (for last item in list) */
  isLast?: boolean;
}

/**
 * Get position label from positionY (1 = GK, 2-4 = DEF, 5-7 = MID, 8-11 = FWD)
 * and translate it using the provided labels
 */
function getPositionLabel(member: FUSportsLineupMember, labels: Labels): string {
  // If API provides a position, normalize it and use as label key
  if (member.position) {
    const positionKey = member.position.toLowerCase().replace(/\s+/g, "");
    return getLabel(labels, `matchPage.lineups.positions.${positionKey}`);
  }
  // Fallback to positionY-based labels
  if (!member.positionY) return "";
  if (member.positionY === 1) return getLabel(labels, "matchPage.lineups.positions.keeper");
  if (member.positionY <= 4) return getLabel(labels, "matchPage.lineups.positions.defender");
  if (member.positionY <= 7) return getLabel(labels, "matchPage.lineups.positions.midfielder");
  return getLabel(labels, "matchPage.lineups.positions.forward");
}

/**
 * Build event decorators for the player image
 * Goals go to bottom-right, cards/substitutions go to bottom-left
 */
function buildEventDecorators(events?: PlayerEvents): EntityDisplayDecorator[] {
  const decorators: EntityDisplayDecorator[] = [];
  if (events) {
    // Left side decorators: cards and substitutions
    if (events.redCard) {
      decorators.push({
        content: <RedCardIcon />,
        position: "bottom-left"
      });
    } else if (events.yellowCard) {
      decorators.push({
        content: <YellowCardIcon />,
        position: "bottom-left"
      });
    }
    // Substitution indicator - out (red) or in (green)
    if (events.substitutedOut) {
      decorators.push({
        content: <SubOutIcon />,
        position: "bottom-left"
      });
    } else if (events.substitutedIn) {
      decorators.push({
        content: <SubInIcon />,
        position: "bottom-left"
      });
    }

    // Right side decorators: goals
    if (events.goals > 0) {
      decorators.push({
        content: <GoalIcon count={events.goals} />,
        position: "bottom-right"
      });
    }
  }
  return decorators;
}

/**
 * Shirt number badge component - used when player photos are disabled
 * Supports decorators in the same positions as the player photo (bottom-left, bottom-right)
 */
const ShirtNumberBadge: React.FC<{
  shirtNumber?: number;
  isHome: boolean;
  decorators?: EntityDisplayDecorator[];
}> = t0 => {
  const $ = _c(6);
  const {
    shirtNumber,
    isHome,
    decorators: t1
  } = t0;
  let t2;
  if ($[0] !== isHome || $[1] !== shirtNumber || $[2] !== t1) {
    const decorators = t1 === undefined ? [] : t1;
    const leftDecorators = decorators.filter(_temp);
    const rightDecorators = decorators.filter(_temp2);
    const t3 = shirtNumber || "?";
    let t4;
    if ($[4] !== t3) {
      t4 = <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-foreground font-bold text-lg">{t3}</div>;
      $[4] = t3;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t2 = <div className={`relative ${isHome ? "mr-3" : "ml-3"}`}>{t4}{leftDecorators.length > 0 && <div className="absolute -bottom-1 -left-1 flex gap-0.5">{leftDecorators.map(_temp3)}</div>}{rightDecorators.length > 0 && <div className="absolute -bottom-1 -right-1 flex gap-0.5">{rightDecorators.map(_temp4)}</div>}</div>;
    $[0] = isHome;
    $[1] = shirtNumber;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};

/**
 * Player row component for lineups display
 */
export const LineupsPlayerRow: React.FC<LineupsPlayerRowProps> = t0 => {
  const $ = _c(49);
  const {
    player,
    isHome,
    labels,
    playerEvents,
    isLast: t1
  } = t0;
  const isLast = t1 === undefined ? false : t1;
  const {
    allowPlayerPhotos
  } = useTenantSettings();
  const {
    playerIdMap
  } = useMatchPageData();
  const nav = useNavigation();
  const nativePlayerId = playerIdMap[player.id];
  let t2;
  if ($[0] !== nativePlayerId || $[1] !== nav) {
    t2 = nativePlayerId ? nav.player(nativePlayerId) : undefined;
    $[0] = nativePlayerId;
    $[1] = nav;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const playerHref = t2;
  let t3;
  if ($[3] !== labels || $[4] !== player) {
    t3 = getPositionLabel(player, labels);
    $[3] = labels;
    $[4] = player;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const posLabel = t3;
  const photoUrl = player.assets?.photoTransparent || player.assets?.photo;
  let decorators;
  let eventDecorators;
  if ($[6] !== allowPlayerPhotos || $[7] !== player.shirtNumber || $[8] !== playerEvents) {
    eventDecorators = buildEventDecorators(playerEvents);
    decorators = [...eventDecorators];
    if (allowPlayerPhotos && player.shirtNumber && !eventDecorators.some(_temp5)) {
      const t4 = String(player.shirtNumber);
      let t5;
      if ($[11] !== t4) {
        t5 = {
          content: t4,
          position: "bottom-right"
        };
        $[11] = t4;
        $[12] = t5;
      } else {
        t5 = $[12];
      }
      decorators.push(t5);
    }
    $[6] = allowPlayerPhotos;
    $[7] = player.shirtNumber;
    $[8] = playerEvents;
    $[9] = decorators;
    $[10] = eventDecorators;
  } else {
    decorators = $[9];
    eventDecorators = $[10];
  }
  const borderClass = isLast ? "" : "border-b border-foreground/10";
  if (!allowPlayerPhotos) {
    const t4 = `py-1.5 flex items-center ${borderClass} ${isHome ? "" : "flex-row-reverse"}`;
    let t5;
    if ($[13] !== eventDecorators || $[14] !== isHome || $[15] !== player.shirtNumber) {
      t5 = <ShirtNumberBadge shirtNumber={player.shirtNumber} isHome={isHome} decorators={eventDecorators} />;
      $[13] = eventDecorators;
      $[14] = isHome;
      $[15] = player.shirtNumber;
      $[16] = t5;
    } else {
      t5 = $[16];
    }
    const t6 = isHome ? "text-left" : "text-right";
    let t7;
    if ($[17] !== player.name || $[18] !== playerHref) {
      t7 = playerHref ? <Link href={playerHref} prefetch={false} className="text-md font-semibold link-neutral hover:underline">{player.name}</Link> : <div className="text-md font-semibold text-foreground">{player.name}</div>;
      $[17] = player.name;
      $[18] = playerHref;
      $[19] = t7;
    } else {
      t7 = $[19];
    }
    let t8;
    if ($[20] !== posLabel) {
      t8 = posLabel && <div className="text-xs text-muted-foreground/60 uppercase">{posLabel}</div>;
      $[20] = posLabel;
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8) {
      t9 = <div className={t6}>{t7}{t8}</div>;
      $[22] = t6;
      $[23] = t7;
      $[24] = t8;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    let t10;
    if ($[26] !== t4 || $[27] !== t5 || $[28] !== t9) {
      t10 = <div className={t4}>{t5}{t9}</div>;
      $[26] = t4;
      $[27] = t5;
      $[28] = t9;
      $[29] = t10;
    } else {
      t10 = $[29];
    }
    return t10;
  }
  const t4 = posLabel || undefined;
  let t5;
  if ($[30] !== photoUrl || $[31] !== player.name) {
    t5 = photoUrl ? {
      imageUrl: photoUrl
    } : {
      image: <PlayerInitialsCircle name={player.name} />
    };
    $[30] = photoUrl;
    $[31] = player.name;
    $[32] = t5;
  } else {
    t5 = $[32];
  }
  const t6 = isHome ? "left" : "right";
  const t7 = decorators.length > 0 ? decorators : undefined;
  const t8 = isHome ? "left" : "right";
  let t9;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      name: "text-md font-semibold text-foreground",
      subtitle: "text-xs text-muted-foreground/60 uppercase"
    };
    $[33] = t9;
  } else {
    t9 = $[33];
  }
  let t10;
  if ($[34] !== t8) {
    t10 = {
      imageSize: "xl",
      imageBorderRadius: "full",
      textAlign: t8,
      typography: t9
    };
    $[34] = t8;
    $[35] = t10;
  } else {
    t10 = $[35];
  }
  let t11;
  if ($[36] !== player.name || $[37] !== t10 || $[38] !== t4 || $[39] !== t5 || $[40] !== t6 || $[41] !== t7) {
    t11 = <EntityDisplay name={player.name} subtitle={t4} {...t5} imagePosition={t6} decorators={t7} styles={t10} />;
    $[36] = player.name;
    $[37] = t10;
    $[38] = t4;
    $[39] = t5;
    $[40] = t6;
    $[41] = t7;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  const entityDisplay = t11;
  const t12 = `py-1.5 ${isHome ? "" : "flex justify-end"} ${borderClass}`;
  let t13;
  if ($[43] !== entityDisplay || $[44] !== playerHref) {
    t13 = playerHref ? <Link href={playerHref} prefetch={false} className="hover:underline">{entityDisplay}</Link> : entityDisplay;
    $[43] = entityDisplay;
    $[44] = playerHref;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  let t14;
  if ($[46] !== t12 || $[47] !== t13) {
    t14 = <div className={t12}>{t13}</div>;
    $[46] = t12;
    $[47] = t13;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  return t14;
};
function _temp(d) {
  return d.position === "bottom-left";
}
function _temp2(d_0) {
  return d_0.position === "bottom-right";
}
function _temp3(d_1, i) {
  return <span key={i}>{d_1.content}</span>;
}
function _temp4(d_2, i_0) {
  return <span key={i_0}>{d_2.content}</span>;
}
function _temp5(d) {
  return d.position === "bottom-right";
}